import styled, { css } from "styled-components";
import DefaultSelect from "react-select";
import DefaultCreatableSelect from "react-select/creatable";

import { COLORS } from "constants/colorScheme";

const styles = {
  control: (provided, props) => ({
    ...provided,
    height: "100%",
    borderWidth: props.isFocused ? "2px" : "1px",
    borderRadius: "4px",
    borderColor: props.selectProps.error
      ? COLORS.RED
      : props.isFocused
      ? COLORS.CYAN
      : COLORS.BLACK_25,
    backgroundColor: props.selectProps.isDisabled
      ? COLORS.BACKGROUND
      : COLORS.WHITE,
    boxShadow: 0,
    minHeight: "54px",
    "&:hover": {
      borderColor: props.selectProps.error
        ? COLORS.RED
        : props.isFocused
        ? COLORS.CYAN
        : COLORS.BLACK_25,
    },
  }),
  placeholder: (provided, props) => ({
    ...provided,
    color: COLORS.BLACK_50,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "100%",
    display: props.selectProps.name
      ? props.isFocused
        ? "block"
        : "none"
      : "block",
  }),
  indicatorsContainer: (provided, props) => ({
    ...provided,
    alignItems: "flex-start",
    padding: "8px 0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  valueContainer: (provided, props) => ({
    ...provided,
    height: "inherit",
    padding: "8px",
  }),
  dropdownIndicator: (provided, props) => ({
    ...provided,
  }),
  input: provided => ({
    ...provided,
    fontFamily: "Roboto",
    fontSize: "16px",
  }),
  menuPortal: provided => ({ ...provided, zIndex: 5 }),
  menu: provided => ({
    ...provided,
    marginTop: 1,
    borderRadius: "0 0 4px 4px",
    border: "none",
  }),
  menuList: provided => ({
    ...provided,
    paddingTop: 10,
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: COLORS.BACKGROUND,
    color: COLORS.FONT_DARK,
  }),
  multiValueLabel: (provided, props) => {
    const hideRemoveIconStyles = props.data?.isFixed
      ? { paddingRight: "6px" }
      : {};

    return { ...provided, ...hideRemoveIconStyles };
  },
  multiValueRemove: (provided, props) => {
    const hideRemoveIconStyles = props.data?.isFixed ? { display: "none" } : {};

    return { ...provided, ...hideRemoveIconStyles };
  },
  option: (provided, props) => {
    const { isFocused, isSelected, isDisabled } = props;
    const backgroundColor =
      (isFocused && isSelected) || isSelected
        ? COLORS.CYAN_DARK
        : isFocused
        ? COLORS.BLACK_4
        : COLORS.WHITE;
    const color =
      (isFocused && isSelected) || isSelected
        ? COLORS.WHITE
        : isFocused
        ? COLORS.CYAN_DARK
        : isDisabled
        ? COLORS.BLACK_50
        : COLORS.FONT_DARK;
    return {
      ...provided,
      padding: "8px 16px",
      backgroundColor,
      color,
      "&:active": { backgroundColor: COLORS.CYAN_20 },
    };
  },
};

const selectStyles = css`
  width: 100%;

  & input::placeholder {
    color: ${COLORS.BLACK_50};
  }
`;

export const Select = styled(DefaultSelect).attrs({ styles })`
  ${selectStyles};
`;

export const CreatableSelect = styled(DefaultCreatableSelect).attrs({ styles })`
  ${selectStyles};
`;

export const Container = styled.div`
  width: 100%;

  position: relative;
`;

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const Label = styled.span`
  top: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  z-index: 1;

  font-size: 16px;
  color: ${COLORS.BLACK_50};

  transform-origin: top left;
  transform: translate(14px, 15px) scale(1);
  transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
    transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;

  ${({ isFloating }: { isFloating: boolean }) =>
    isFloating &&
    css`
      z-index: 1;
      transform: translate(14px, -9px) scale(0.75);
      padding: 0 5px;

      color: ${COLORS.CYAN}
      background-color: ${COLORS.WHITE};
    `}

  ${({ hasValue, hasError }: { hasValue: boolean, hasError: boolean }) =>
    hasError
      ? css`
          color: ${COLORS.RED};
        `
      : hasValue
      ? css`
          color: ${COLORS.BLACK_50};
        `
      : css``}
`;

export const OptionContainer = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;

  width: 100%;
`;

export const MultiValuePlaceholder = styled.div`
  color: ${COLORS.BLACK_50};
  margin-left: 2px;
  margin-right: 2px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  box-sizing: border-box;

  &:not(:first-child) {
    display: none;
  }
`;

export const MultiValueContainer = styled.div`
  display: flex;
  position: relative;
`;

export const MultiValues = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${({ withClearButton }: { withClearButton?: boolean }) =>
    withClearButton &&
    css`
      padding-right: 80px;
    `}
`;

export const MultiValue = styled.div`
  border-radius: 6px;
  padding: 6px 8px 7px 12px;
  background-color: ${COLORS.BACKGROUND};

  display: flex;
  align-items: center;

  margin-right: 12px;
  margin-top: 12px;

  ${({ hideClearIcon }: { hideClearIcon?: boolean }) =>
    hideClearIcon &&
    css`
      padding: 6px 12px 7px 12px;

      ${MultiValueClear} {
        display: none;
      }
    `}
`;

export const MultiValueClear = styled.span`
  display: flex;
  margin-left: 8px;

  cursor: pointer;
`;

export const ClearButtonContainer = styled.div`
  display: flex;

  position: absolute;
  right: 0;

  margin-top: 12px;
`;
